import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { createColumnHelper, useReactTable, getCoreRowModel, getPaginationRowModel, getSortedRowModel, flexRender } from '@tanstack/react-table';
import { saveAs } from 'file-saver';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronsUpDown, ChevronUp, Download, Maximize, Search, Shrink, X } from 'lucide-react';

type DynamicTableProps = {
  jsonData: string;
};

const DynamicTable: React.FC<DynamicTableProps> = ({ jsonData }) => {
  const [data, setData] = useState<any[]>([]);
  const [filter, setFilter] = useState('');
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [selectedCellContent, setSelectedCellContent] = useState<string | null>(null);
  const [modalPosition, setModalPosition] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => { 
    setData([]); // iniciar data vaicia
    try {
      const cleanedJson = jsonData
                      .replace(/\n/g, " ") //saltos de linea
                      .replace(/NaN/g, 'null') // Reemplazar NaN
                      .replace(/\u00a0/g, ''); // Eliminar espacios no visibles

      const parsedData = JSON.parse(cleanedJson); 
      setData(parsedData); 
    } catch (error) {
      console.error("Datos JSON inválidos", error);
    }
  }, [jsonData]);

  const columns = useMemo(() => {
    if (data?.length === 0) return [];
    const columnHelper = createColumnHelper<any>(); 
    const indexColumn = columnHelper.display({
      id: 'index',
      header: '#',
      cell: (info) => info.row.index + 1, 
    }); 
    const generatedColumns = Object.keys(data?data[0]:{}).map((key) =>
      columnHelper.accessor(key, {
        header: (key.charAt(0).toUpperCase() + key.toLowerCase().slice(1)).replace(/\n/g, " ")
      })
    ); 
    return [indexColumn, ...generatedColumns];
  }, [data]);
  const columnsOrigin = useMemo(() => {
    if (data?.length === 0) return [];
    const columnHelper = createColumnHelper<any>(); 
    const indexColumn = columnHelper.display({
      id: 'index',
      header: '#',
      cell: (info) => info.row.index + 1, 
    }); 
    const generatedColumns = Object.keys(data?data[0]:{}).map((key) =>
      columnHelper.accessor(key, {
        header: key, 
      })
    ); 
    return [indexColumn, ...generatedColumns];
  }, [data]);
 

  const filteredData = useMemo(() => {
    if (!filter) return data;
    const lowercasedFilter = filter.toLowerCase();
    return data.filter((row) =>
      Object.values(row).some(
        (value) => value && value.toString().toLowerCase().includes(lowercasedFilter)
      )
    );
  }, [data, filter]);

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      pagination: {
        pageSize: 5, // Establece 10 filas por página
      },
    },
  });

  const handleDownloadCSV = useCallback(() => {
    const formatCell = (value: string | number | undefined | null) => {
      if (value === null || value === undefined) return '';
      const stringValue = value.toString(); 
      return stringValue
        .replace(/\n/g, " ")  
        .replace(/;/g, ',');
    };

    const headers = columnsOrigin.map(col => 
        `" ${ col.header?formatCell(col.header?.toString().charAt(0).toUpperCase() + 
              col.header?.toString().toLowerCase().slice(1)
            ):''  }"`).join(';');
    const rows = data.map((row, indexRow) =>
      columnsOrigin.map((col, indexCol) => 
          `" ${ indexCol == 0?indexRow+1: formatCell(row[ col.header?col.header.toString():''  ] || '') }"` ).join(';')
    );
    
    const csv = [headers, ...rows].join('\n'); 

    const BOM = '\uFEFF'; // Byte Order Mark
    const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });

    //const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'Promociones.csv');
  }, [columnsOrigin, data]);

  const toggleFullScreen = useCallback(() => {
    setIsFullScreen(!isFullScreen);
    if(isFullScreen){
      table.setPageSize(5);
    }else{
      table.setPageSize(15);
    }
    
  }, [isFullScreen]);

  const handleCellClick = useCallback((content: string, event: React.MouseEvent<HTMLTableCellElement>) => {
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    if (selectedCellContent === content) {
      closeModal();
    } else {
      setSelectedCellContent(content);
      setModalPosition({ top: rect.top + window.scrollY, left: rect.left + window.scrollX });
    }
  }, [selectedCellContent]);

  const closeModal = useCallback(() => {
    setSelectedCellContent(null);
    setModalPosition(null);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectedCellContent && !document.getElementById('modal-cell')?.contains(event.target as Node)) {
        closeModal();
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [selectedCellContent, closeModal]);

// buscador 
    const [showSearch, setShowSearch] = useState(false);
    const toggleSearch = () => setShowSearch(!showSearch);
 
   

  return ( data && 
    <div className={` ${isFullScreen ? 'fixed top-0 left-0 w-full h-full bg-white z-50 animate-fadeIn' : ''}`}>
        <div className="flex justify-end items-center">
            <button 
                onClick={handleDownloadCSV} 
                className="p-1 bg-gray-100 text-slate-900 rounded-lg mr-2 shadow-lg hover:shadow-xl hover:bg-slate-200"
            >
                <Download strokeWidth={0.5} size={18} />
            </button>
            <div className="relative">
              <button 
                onClick={toggleSearch} 
                className="p-1 bg-gray-100 text-slate-900 rounded-lg mr-2 shadow-lg hover:shadow-xl hover:bg-slate-200"
              >
                    <Search strokeWidth={0.5} size={18}/>
              </button>
              {showSearch && (
                <div className="p-2 w-64 border absolute top-full mt-1 shadow-lg right-0 -top-2 bg-white flex space-x-2 border n-fmr-input  border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-700 animate-fadeIn">
                    <input 
                    type="text"
                    placeholder="Buscar..."
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)} 
                    className="flex-grow outline-none text-sm px-2"
                    /> 
                    <X strokeWidth={0.8} size={18}
                    className="text-slate-500 hover:text-slate-800 cursor-pointer" 
                    onClick={() => {toggleSearch();setFilter('');} } 
                    /> 
                </div> 
              )}
            </div> 
            <button onClick={toggleFullScreen} 
              className="p-1 bg-gray-100 text-slate-900 rounded-lg mr-2 shadow-lg hover:shadow-xl hover:bg-slate-200"> 
                {isFullScreen ? <Shrink strokeWidth={0.5} size={18} /> : <Maximize strokeWidth={0.5} size={18} />}
            </button>
        </div>
        <div className="overflow-x-auto scroll-custom-h">
            <table className="w-full text-left border-collapse">
                <thead>
                {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id} className="bg-gray-100">
                    {headerGroup.headers.map(header => (
                        <th
                        key={header.id}
                        onClick={header.column.getToggleSortingHandler()}
                        className=" px-4 text-xs border-b font-semibold cursor-pointer text-gray-700"
                        >
                            <div className="flex items-center justify-between w-full">
                            
                                {flexRender(header.column.columnDef.header, header.getContext())}
                                {header.column.getIsSorted()
                                    ? (header.column.getIsSorted() === 'asc' ? <ChevronUp strokeWidth={0.5} size={15} className="text-slate-900" /> : <ChevronDown strokeWidth={0.5} size={15} className="text-slate-900"/>)
                                    : (header.id=='index'?'':<ChevronsUpDown strokeWidth={0.5} size={15} className="text-slate-900" />)
                                }
                            </div>
                        </th>
                    ))}
                    </tr>
                ))}
                </thead>
                <tbody>
                {table.getRowModel().rows.map(row => (
                    <tr key={row.id} className="hover:bg-gray-50">
                    {row.getVisibleCells().map(cell => (
                        <td
                        key={cell.id}
                        className="px-4 text-xs border-b text-gray-600 truncate max-w-xs cursor-pointer"
                        onClick={(e) => handleCellClick(cell.getValue() as string, e)}
                        style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                        title={cell.getValue() as string}
                        >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                    ))}
                    </tr>
                ))}
                </tbody>
            </table>
        </div> 
        {(table.getPageCount() > 1)&&( 
            <div className="flex justify-end items-center mt-1">
            {table.getCanPreviousPage()&&(
                <button
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                    className="p-1 border bg-slate-50 text-slate-400 rounded-full disabled:opacity-50 mr-2 shadow-lg hover:shadow-xl hover:bg-white hover:border-blue-400"
                >
                    <ChevronLeft />
                </button> 
            )} 
            <span className="p-2 pt-2 pb-2 bg-blue-700 text-white rounded-full disabled:opacity-50 mr-2">
                {table.getState().pagination.pageIndex + 1} / {' '}
                {table.getPageCount()} 
            </span>
            {table.getCanNextPage()&&(
                <button
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                    className="p-1 border bg-slate-50 text-slate-400 rounded-full disabled:opacity-50 mr-2 shadow-lg hover:shadow-xl hover:bg-white hover:border-blue-400"
                >
                    <ChevronRight />
                </button>
            )} 
            </div> 
        )} 


        {selectedCellContent && modalPosition && (
        <div
            id="modal-cell"
            style={{ position: 'absolute', top: modalPosition.top, left: modalPosition.left }}
            className="bg-white p-2 border rounded shadow-lg mr-5"
        >
            <p className="text-gray-700">{selectedCellContent}</p>
        </div>
        )}
    </div>
  );
};

export default DynamicTable;
